import { Close } from '@mui/icons-material';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Stack,
} from '@mui/material';
import { useTranslation } from '../../../contexts/TranslationContext';
import { device_controller } from '../../../controllers/device_controller';
import { deviceListEntryInterface } from '../../../models/interfaces/device_interfaces';
import { MHMIconButton } from '../MHM/MHMIconButton';
import { MHMLabel } from '../MHM/MHMLabel';
import { MHMTypography } from '../MHM/MHMTypography';
import { formatImageURL } from './DeviceImages';
import { ALERT_LABELS } from './DeviceList';
import { useRecoilValue } from 'recoil';
import { deviceModeAtom } from '../../../models/atoms/device_atoms';
import { unix_timestamp_to_date_time } from '../../../utils/helper_functions';

interface DeviceInfoCardProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  device: deviceListEntryInterface;
  onClose?: () => void;
}

export const DeviceInfoCard = (props: DeviceInfoCardProps) => {
  const device: deviceListEntryInterface = props.device;
  const { t, language } = useTranslation();
  const mode = useRecoilValue(deviceModeAtom);

  const startDate = device.creation_date
    ? unix_timestamp_to_date_time(
        device.creation_date ?? 0,
        false,
        true,
        false,
        language,
      ).toLocaleString()
    : t('Unknown');

  const endDate = device.end_date
    ? unix_timestamp_to_date_time(
        device.end_date ?? 0,
        false,
        true,
        false,
        language,
      ).toLocaleString()
    : t('Unknown');

  return (
    <Card sx={{ overflow: 'visible', p: 1 }}>
      {props.onClose && (
        <MHMIconButton
          onClick={props.onClose}
          sx={{
            position: 'absolute',
            top: '-0.1em',
            left: '-0.1em',
            p: 0,
          }}
        >
          <Close fontSize="large" sx={{ width: '0.7em', height: '0.7em' }} />
        </MHMIconButton>
      )}
      <CardActionArea
        onClick={() =>
          device_controller.select_device(device.device_id, device.session_id)
        }
      >
        {device.thumbnail && (
          <CardMedia
            sx={{ height: 140 }}
            image={formatImageURL(device.thumbnail)}
            title="Device Image"
            component="img"
            loading="lazy"
          />
        )}
        <CardContent>
          {device.client_identifier ? (
            <>
              <MHMTypography gutterBottom variant="h5" component="div">
                {device.client_identifier}
              </MHMTypography>
              <MHMTypography variant="body2">
                {t('Device ID')}: {device.device_id}
              </MHMTypography>
            </>
          ) : (
            <MHMTypography gutterBottom variant="h5" component="div">
              {t('Device ID')}: {device.device_id}
            </MHMTypography>
          )}
          {mode === 'live' ? (
            <MHMTypography variant="body2">
              {t('Fill')}: {device.current_level_percentage.toFixed(1)}%
            </MHMTypography>
          ) : (
            <>
              <MHMTypography variant="body2">
                {t('Start Date')}: {startDate}
              </MHMTypography>
              <MHMTypography variant="body2">
                {t('End Date')}: {endDate}
              </MHMTypography>
            </>
          )}
          <Stack gap={1} alignItems="center">
            {device.labels.map(l => (
              <MHMLabel
                key={l}
                label={t(l)}
                labelProps={{ bold: true }}
                color={ALERT_LABELS.includes(l) ? 'error' : 'primary'}
                sx={{ flexBasis: 0, maxWidth: 'fit-content' }}
              />
            ))}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
